import React, { useState } from 'react'

import { clsx } from 'clsx'

import {
  Badge as BadgeType,
  BadgeType as BadgeTypeType,
  Media,
} from '@tribeplatform/gql-client/types'
import { EntityImage } from '@tribeplatform/react-components/common/components'
import { Form } from '@tribeplatform/react-components/Form'
import { T, useI18n, useStaticIntl } from '@tribeplatform/react-components/i18n'
import {
  getBadgeSetting,
  MemberBadgeLocation,
} from '@tribeplatform/react-components/MemberBadge'
import { PopoverImagePicker } from '@tribeplatform/react-components/Picker'
import { handleUploadFileError } from '@tribeplatform/react-components/utils'
import {
  useCreateBadge,
  useCreateEmojis,
  useUpdateBadge,
  useImageUploader,
} from '@tribeplatform/react-sdk/hooks'
import { Alert } from '@tribeplatform/react-ui-kit/Alert'
import { Badge } from '@tribeplatform/react-ui-kit/Badge'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { useDocument } from '@tribeplatform/react-ui-kit/hooks'
import { Modal } from '@tribeplatform/react-ui-kit/Modal'
import { Tabs } from '@tribeplatform/react-ui-kit/Tabs'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import {
  badgeStylesFactory,
  locationOptionsFactory,
  smartBadgeNameMappingFactory,
} from './consts.js'

const BadgeStyle = ({ onClick, badgeStyle, active, text }) => {
  return (
    <div className="p-1 inline-block">
      <button
        type="button"
        className={clsx(
          'flex items-center p-2 border rounded-base',
          active
            ? 'border-line-pressed'
            : 'border-transparent hover:border-line-hovered',
        )}
        onClick={onClick}
      >
        <Badge
          style={{
            backgroundColor: badgeStyle.bg,
            color: badgeStyle.text,
          }}
        >
          {text}
        </Badge>
      </button>
    </div>
  )
}

export const BadgeModal = ({
  open,
  onClose,
  badge,
}: {
  open: boolean
  onClose: () => void
  badge: BadgeType
}) => {
  const { document } = useDocument()
  const intl = useI18n()
  const { $t } = intl
  const smartBadges = useStaticIntl(smartBadgeNameMappingFactory)
  const locationOptions = useStaticIntl(locationOptionsFactory)
  const isNew = !badge?.id
  const isSmart = badge?.type && badge?.type !== BadgeTypeType.Manual
  const modalTitle = isNew
    ? $t({ defaultMessage: 'Add badge', id: 'Generics.AddBadge' })
    : $t({ defaultMessage: 'Update badge', id: 'Generics.UpdateBadge' })
  const { mutateAsync: createBadge, isLoading: isCreating } = useCreateBadge()
  const { mutateAsync: updateBadge, isLoading: isUpdating } = useUpdateBadge()
  const { mutateAsync: createEmojis } = useCreateEmojis()
  const { uploadImage } = useImageUploader()

  const [style, setStyle] = useState(getBadgeSetting(badge, 'style'))
  const initialLocations = isNew
    ? [MemberBadgeLocation.ProfileName]
    : getBadgeSetting(badge, 'locations') || []

  const [image, setImage] = useState<Media>(badge.image)

  const onSubmit = data => {
    data = {
      ...data,
      ...{
        settings: [
          { key: 'style', value: JSON.stringify(style) },
          {
            key: 'locations',
            value: JSON.stringify(
              data?.locations?.map(
                text => locationOptions.find(l => l.text === text)?.value,
              ) || [],
            ),
          },
        ],
      },
    }
    delete data.locations

    if (isNew) {
      data.active = true
      return createBadge({ input: data }).then(() => onClose())
    }

    const { id, ...input } = data
    delete input.image
    delete input.type
    return updateBadge({ id, input }).then(() => onClose())
  }

  const onStatusChanged = async active => {
    updateBadge({
      id: badge.id,
      input: {
        active,
        shortDescription: badge.shortDescription,
      },
    })
      .then(() => {
        if (active) {
          toast({
            title: $t(
              {
                defaultMessage: '{badge_name} badge is now enabled',
                id: 'Admin.Badge.Enabled',
              },
              {
                badge_name: badge.name,
              },
            ),
          })
        } else {
          toast({
            title: $t(
              {
                defaultMessage: '{badge_name} badge is now disabled',
                id: 'Admin.Badge.Disabled',
              },
              {
                badge_name: badge.name,
              },
            ),
          })
        }
      })
      .catch(() => {
        toast({
          title: $t(
            {
              defaultMessage: 'Unable to update {badge_name} badge',
              id: 'Admin.Badge.UnableToUpdate',
            },
            {
              badge_name: badge.name,
            },
          ),
          status: 'error',
        })
      })

    onClose()
  }

  const onUploadImage = async (
    files: Array<File>,
    onSuccess: (image: Media) => void,
  ) => {
    const file = files[0]

    try {
      const uploadedImage = await uploadImage(file)
      onSuccess(uploadedImage)
    } catch (error) {
      handleUploadFileError(intl)(error)
    }
  }

  const badgeStyles = badgeStylesFactory(document)

  return (
    <Modal open={open} onClose={onClose} size="2xl">
      <Modal.Header title={modalTitle} />
      <Modal.Content>
        <Form
          onSubmit={onSubmit}
          defaultValues={{
            ...badge,
            ...{
              locations: initialLocations.map(
                value => locationOptions.find(l => l.value === value)?.text,
              ),
            },
          }}
          className="flex flex-col space-y-3"
        >
          {({ watch, reset, getValues, setValue }) => {
            const [name, backgroundColor = '#f3f4f6', textColor = '#1f2937'] =
              watch(['name', 'backgroundColor', 'textColor'])

            return (
              <>
                {isSmart && (
                  <Alert status="info">
                    <p>
                      <T
                        defaultMessage="This is {is_known_smart_badge, select, true {the {badge_name}} other {a}} smart badge"
                        id="Admin.MemberBadges.IsSmartBadge"
                        values={{
                          is_known_smart_badge: !!smartBadges[badge?.type],
                          badge_name: (
                            <strong>{smartBadges[badge?.type]}</strong>
                          ),
                        }}
                      />
                    </p>
                    <p>
                      <T
                        defaultMessage="Smart badges are automatically assigned and unassigned."
                        id="Admin.MemberBadges.SmartBadgesDescription"
                      />
                    </p>
                  </Alert>
                )}
                <Form.Input
                  name="name"
                  label={$t({
                    defaultMessage: 'Name',
                    id: 'Generics.Name.Noun',
                  })}
                />
                <Form.Input
                  name="shortDescription"
                  label={$t({
                    defaultMessage: 'Short description',
                    id: 'Generics.ShortDescription',
                  })}
                  helperText={$t({
                    defaultMessage: 'Will be shown as a tooltip on hover',
                    id: 'Admin.MemberBadges.ShortDescription.HelperText',
                  })}
                />
                <Form.Multiselect
                  label={$t({
                    defaultMessage: 'Show this badge in',
                    id: 'Admin.MemberBadges.DisplayLocations',
                    description:
                      'Label for a multiselect field where admins can determine in which locations a member badge is displayed.',
                  })}
                  name="locations"
                  options={locationOptions.map(location => location.text)}
                />
                <FormControl>
                  <FormControl.Label>
                    <T
                      defaultMessage="Badge style"
                      id="Admin.MemberBadges.Style"
                    />
                  </FormControl.Label>
                  <Tabs.Group
                    defaultIndex={style === 'image' ? 1 : 0}
                    onChange={index => {
                      setStyle(index === 0 ? 'text' : 'image')
                    }}
                  >
                    <Tabs.List
                      variant="filled"
                      fullWidth
                      className="bg-surface-subdued rounded-base p-1 my-2"
                    >
                      <Tabs.Tab>
                        <T
                          defaultMessage="Text badge"
                          id="Admin.MemberBadges.Style.Text"
                        />
                      </Tabs.Tab>
                      <Tabs.Tab>
                        <T
                          defaultMessage="Image badge"
                          id="Admin.MemberBadges.Style.Image"
                        />
                      </Tabs.Tab>
                    </Tabs.List>
                    <Tabs.Panels>
                      <Tabs.Panel>
                        {badgeStyles.map(style => {
                          const active =
                            style.bg === backgroundColor &&
                            style.text === textColor

                          return (
                            <BadgeStyle
                              key={style.bg}
                              onClick={() => {
                                const values = getValues()
                                values.backgroundColor = style.bg
                                values.textColor = style.text
                                reset(values)
                              }}
                              active={active}
                              badgeStyle={style}
                              text={name || 'Badge'}
                            />
                          )
                        })}
                      </Tabs.Panel>
                      <Tabs.Panel>
                        <div className="flex w-full items-center p-5 justify-center">
                          <PopoverImagePicker
                            tabs={['emoji', 'upload', 'link', 'unsplash']}
                            sizeConfig={{
                              recommendedWidth: 512,
                              recommendedHeight: 512,
                              showSizeLimit: false,
                            }}
                            onImageChange={async files => {
                              await onUploadImage(files, image => {
                                if (image?.id) {
                                  setImage(image)
                                  setValue('imageId', image.id)
                                }
                              })
                            }}
                            onEmojiChange={async emoji => {
                              const uploadedEmoji = await createEmojis([
                                { text: emoji?.id },
                              ])
                              setImage(uploadedEmoji[0])
                              setValue(
                                'imageId',
                                uploadedEmoji[0].id || emoji.id,
                              )
                            }}
                          >
                            <div className="flex flex-col space-y-3 items-center">
                              <EntityImage
                                size="lg"
                                entity={{
                                  image,
                                  name: $t({
                                    defaultMessage: 'Badge image',
                                    id: 'Admin.MemberBadges.Image',
                                  }),
                                }}
                              />
                              <Button variant="secondaryNeutral">
                                <T
                                  defaultMessage="Pick image"
                                  id="Generics.PickImage"
                                />
                              </Button>
                            </div>
                          </PopoverImagePicker>
                        </div>
                      </Tabs.Panel>
                    </Tabs.Panels>
                  </Tabs.Group>
                </FormControl>
                <Form.Actions className="pt-3">
                  <Button
                    loading={isCreating || isUpdating}
                    size="lg"
                    type="submit"
                  >
                    {isNew ? (
                      <T defaultMessage="Create" id="Generics.Create" />
                    ) : (
                      <T defaultMessage="Update" id="Generics.Update" />
                    )}
                  </Button>
                  {!isNew && (
                    <Button
                      size="lg"
                      variant="secondaryNeutral"
                      type="button"
                      onClick={() => onStatusChanged(!badge.active)}
                    >
                      {badge?.active ? (
                        <T defaultMessage="Disable" id="Generics.Disable" />
                      ) : (
                        <T defaultMessage="Enable" id="Generics.Enable" />
                      )}
                    </Button>
                  )}
                </Form.Actions>
              </>
            )
          }}
        </Form>
      </Modal.Content>
    </Modal>
  )
}
